/**
 * THE BILLING WALL IS DERIVED, NOT DECLARED (multi-product 023 G57 (3), G55;
 * 018 §8 P4b, WP3.13.9a).
 *
 * Opening an app and paying for it are two facts (G55). Which apps a scope
 * opens is its app access cells (entitlements, `app-access-cells.ts`); which of its surfaces wall a firm
 * that has not paid is this module, and it reads billing facts only:
 *
 * | port            | floor | wall           |
 * |-----------------|-------|----------------|
 * | `none`          | —     | `none`         |
 * | billed          | yes   | `paid-rungs` (the floor stays open) |
 * | billed          | no    | `all-surfaces` (the payment wall) |
 *
 * A scope's wall is the rule over its row's `planPort` and `floor`, and only
 * once the scope bills on its OWN catalogue (`catalogueScope === scope`, the
 * P-bill fact of R-018-8). A scope billed through another scope's catalogue
 * (`business` until it sells on Teams, 018 P6) has no wall of its own: the
 * catalogue it bills through walls it. That is what keeps every row's wall
 * byte-identical to the value the registry used to declare, and what flips
 * `business` to `all-surfaces` by itself when WP3.2.4 gives it its catalogue.
 *
 * Whether the wall BITES for one firm (no active subscription, locked out) is
 * the account's payment state, read at request time by the billing gate; this
 * is the scope-level half. Type-only imports from `platform-scope`, which
 * imports this module, so there is no runtime cycle.
 */
import type { BillingWallPolicy, PlanFloor, PlanPortKey } from "./platform-scope"

/** Whether a port bills a firm on an account of its own (`none` never does). */
export const portBills = (port: PlanPortKey): boolean => port !== "none"

/** 023 G57 (3): the one billing-wall rule, over a port and its floor. */
export const billingWallOfPort = (port: PlanPortKey, floor: PlanFloor | null): BillingWallPolicy => {
  if (!portBills(port)) return "none"
  return floor === null ? "all-surfaces" : "paid-rungs"
}

/** The billing facts of one registry row that decide its wall. */
export interface ScopeWallFacts {
  readonly planPort: PlanPortKey
  readonly floor: PlanFloor | null
  /** `catalogueScope === scope`: the scope sells through a catalogue of its own. */
  readonly ownCatalogue: boolean
}

/** A scope's wall: the port rule once it bills on its own catalogue, else `none`. */
export const scopeBillingWall = (facts: ScopeWallFacts): BillingWallPolicy =>
  facts.ownCatalogue ? billingWallOfPort(facts.planPort, facts.floor) : "none"
